/**
 * PermitReportCardModal — shows a project's plan check report card and copies it for the end of an
 * email to a plan checker (docs/PERMIT_REPORT_CARD.md). Mounted once in OutlineView; opened by
 * CustomEvent('open-permit-report-card', { projectId }) from the Permits ⋯ menu and an agency
 * review's ⋯ menu. The card is built by services/permitReportCard.js; this window only shows it.
 */
import { useEffect, useState } from 'react'
import { getProjectReportCard, renderReportCard, copyReportCard } from '../services/permitReportCard.js'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'

export default function PermitReportCardModal() {
  const [open, setOpen] = useState(false)
  const [card, setCard] = useState(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    async function onOpen(e) {
      const projectId = (e && e.detail && e.detail.projectId) || ctx.recordId || wf.zoomProjectId
      setOpen(true); setCard(null); setError(''); setCopied(false)
      if (!projectId) { setError('Open a project first.'); return }
      try { setCard(await getProjectReportCard(projectId)) } catch (err) { setError(err.message || 'The report card could not be built.') }
    }
    window.addEventListener('open-permit-report-card', onOpen)
    return () => window.removeEventListener('open-permit-report-card', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null
  const rendered = card && card.rows.length ? renderReportCard(card) : null

  async function copy() {
    setError('')
    try { await copyReportCard(card); setCopied(true) } catch (err) { setError('Could not copy: ' + (err.message || err)) }
  }

  return (
    <div style={S.backdrop} onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}>
      <div data-modal-id="PermitReportCardModal" style={S.box}>
        <div style={S.title}>Plan check report card</div>
        <div style={S.intro}>
          Paste this at the end of every email to a plan checker on this project. It holds each agency to
          its own published review time, counted from the day the plan check fees were paid.
        </div>
        {!card && !error && <div style={S.muted}>Building the card…</div>}
        {card && !card.rows.length && <div style={S.muted}>No review on this project is with an agency right now.</div>}
        {rendered && <div style={S.paper} dangerouslySetInnerHTML={{ __html: rendered.html }} />}
        {error && <div style={S.error}>{error}</div>}
        <div style={S.actions}>
          {copied && <span style={S.done}>Copied. Paste it at the end of your email.</span>}
          <button style={S.btn} onClick={() => setOpen(false)}>Close</button>
          <button style={S.primary} disabled={!rendered} onClick={copy}>Copy report card</button>
        </div>
      </div>
    </div>
  )
}

const ff = '"DM Sans",sans-serif'
const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  box: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 620, maxWidth: 'calc(100vw - 32px)', padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', fontFamily: ff, color: 'var(--aj-text)', display: 'flex', flexDirection: 'column', gap: 12 },
  title: { fontSize: 13, fontWeight: 600 },
  intro: { fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.5 },
  muted: { fontSize: 12, color: 'var(--aj-text-muted)' },
  // The card is email content, so it previews on white whatever the theme.
  paper: { background: '#fff', borderRadius: 6, padding: 14, overflowX: 'auto' },
  error: { color: '#c0392b', fontSize: 12 },
  done: { fontSize: 12, color: 'var(--aj-accent)', marginRight: 'auto' },
  actions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 },
  btn: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  primary: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
}
